import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import routes_api from '../../connections/routes_api';
import { usePlannerCalendars } from './moduleServices/usePlannerCalendars';
import useOfflineFirstData from 'services/useOfflineFirstData';
import {
  parsePlannerEventsListResponse,
  STORAGE_KEY_PLANNER_EVENTS_LIST_WINDOW,
} from './moduleServices/planner_events_list_window';
import NavbarTop from './moduleComponents/NavbarTop';
import NavbarBottom from './moduleComponents/NavbarBottom';
import MainTodo from './tab_todo/main_todo';
import MainEvents from './tab_events/main_events';
import MainCalendar from './tab_calendar/main_calendar';
import TabPlannerSettings from './tab_settings/main_settings';
import MainProjects from './tab_projects/main_projects';
import {
  loadWeekRowsVisibleFromStorage,
  persistWeekRowsVisible,
} from './moduleServices/planner_calendar_week_rows';
import {
  loadPlannerTimeFormatFromStorage,
  persistPlannerTimeFormat,
} from './moduleServices/planner_time_format';
import {
  filterCalendarsForTodoTab,
  filterCalendarsExcludingTodoType,
} from './moduleServices/planner_calendar_types';

const STORAGE_KEY_FADE_PAST = 'planner_fade_past_events';

function getStoredFadePast() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FADE_PAST);
    if (stored === null) return true;
    return stored !== 'false';
  } catch {
    return true;
  }
}

const VALID_TABS = new Set(['todo', 'events', 'calendar', 'settings', 'projects']);

const PlannerMain = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('todo');
  const [fadePastEvents, setFadePastEvents] = useState(getStoredFadePast);
  const [weekRowsVisible, setWeekRowsVisibleState] = useState(loadWeekRowsVisibleFromStorage);
  const [projectsCanvasMaxWidth, setProjectsCanvasMaxWidth] = useState(true);
  const [timeFormatPreference, setTimeFormatPreferenceState] = useState(
    loadPlannerTimeFormatFromStorage
  );

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && VALID_TABS.has(tab)) {
      setActiveTab(tab);
    }
    const gc = searchParams.get('google_connected');
    const ge = searchParams.get('google_error');
    if (gc !== '1' && ge !== '1') return;
    if (gc === '1') toast.success('Google Calendar connected.');
    if (ge === '1') toast.error('Google connection failed.');
    const next = new URLSearchParams(searchParams);
    next.delete('google_connected');
    next.delete('google_error');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleFadePastChange = (value) => {
    setFadePastEvents(value);
    try {
      localStorage.setItem(STORAGE_KEY_FADE_PAST, value ? 'true' : 'false');
    } catch {
      // ignore
    }
  };

  const handleWeekRowsVisibleChange = (n) => {
    if (n !== 5 && n !== 6) return;
    setWeekRowsVisibleState(n);
    persistWeekRowsVisible(n);
  };

  const handleTimeFormatChange = (mode) => {
    setTimeFormatPreferenceState(mode);
    persistPlannerTimeFormat(mode);
  };

  // Use offline-first hook for events
  const {
    data: eventsData,
    listMeta: plannerEventsWindow,
    isLoading: isEventsLoading,
    refresh: refreshEvents,
    updateOptimistically: updateEventsOptimistically,
  } = useOfflineFirstData(routes_api.planner_events, {
    parseListPayload: parsePlannerEventsListResponse,
    listMetaLocalStorageKey: STORAGE_KEY_PLANNER_EVENTS_LIST_WINDOW,
  });

  // Sort by start (timed or all-day date)
  const events = useMemo(() => {
    if (!eventsData || eventsData.length === 0) return [];
    return [...eventsData].sort((a, b) => {
      const dateA = new Date(a.start_datetime || a.start_date || 0).getTime();
      const dateB = new Date(b.start_datetime || b.start_date || 0).getTime();
      return dateA - dateB; // Ascending order (oldest first)
    });
  }, [eventsData]);

  // Use hook for calendars management
  const {
    calendars,
    isLoading: isCalendarsLoading,
    createCalendar: handleCalendarCreate,
    updateCalendar: handleCalendarUpdate,
    deleteCalendar: handleCalendarDelete,
    fetchCalendars: refreshCalendars,
  } = usePlannerCalendars();

  const calendarsTodoTabOnly = useMemo(() => filterCalendarsForTodoTab(calendars), [calendars]);
  const calendarsExcludingTodoType = useMemo(
    () => filterCalendarsExcludingTodoType(calendars),
    [calendars],
  );

  // Tab change handler
  const handleTabChange = (tab) => setActiveTab(tab);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div
        className={`w-full mx-auto pb-20 ${
          activeTab === 'projects'
            ? `${projectsCanvasMaxWidth ? 'max-w-[min(100vw,90rem)] px-1 sm:px-2' : 'max-w-none px-6'} pt-20`
            : 'max-w-5xl pt-14'
        }`}
      >
        {/* Tab panels */}
        {activeTab === 'todo' && <MainTodo calendars={calendarsTodoTabOnly} />}
        {activeTab === 'events' && (
          <MainEvents
            events={events}
            isEventsLoading={isEventsLoading}
            refreshEvents={refreshEvents}
            updateEventsOptimistically={updateEventsOptimistically}
            calendars={calendarsExcludingTodoType}
            fadePastEvents={fadePastEvents}
            timeFormatPreference={timeFormatPreference}
          />
        )}
        {activeTab === 'calendar' && (
          <MainCalendar
            events={events}
            eventsWindow={plannerEventsWindow}
            isEventsLoading={isEventsLoading}
            refreshEvents={refreshEvents}
            updateEventsOptimistically={updateEventsOptimistically}
            calendars={calendarsExcludingTodoType}
            weekRowsVisible={weekRowsVisible}
            timeFormatPreference={timeFormatPreference}
          />
        )}
        {activeTab === 'projects' && (
          <MainProjects onCanvasMaxWidthChange={setProjectsCanvasMaxWidth} />
        )}
        {activeTab === 'settings' && (
          <TabPlannerSettings
            calendars={calendars}
            isCalendarsLoading={isCalendarsLoading}
            onCalendarCreate={handleCalendarCreate}
            onCalendarUpdate={handleCalendarUpdate}
            onCalendarDelete={handleCalendarDelete}
            fadePastEvents={fadePastEvents}
            onFadePastEventsChange={handleFadePastChange}
            weekRowsVisible={weekRowsVisible}
            onWeekRowsVisibleChange={handleWeekRowsVisibleChange}
            timeFormatPreference={timeFormatPreference}
            onTimeFormatPreferenceChange={handleTimeFormatChange}
            refreshCalendars={refreshCalendars}
            refreshEvents={refreshEvents}
          />
        )}
      </div>
      {/* Navbar Top */}
      <NavbarTop
        title="Planner"
        onHomeClick={() => navigate('/app/')}
        handleTabChange={handleTabChange}
        activeTab={activeTab}
      />
      {/* Navbar Bottom */}
      <NavbarBottom activeTab={activeTab} handleTabChange={handleTabChange} />
    </div>
  );
};

export default PlannerMain;
